import { useEffect, useRef } from 'react'

import { useSessionStore } from '@/lib/auth/session-store'
import { currentCoordinates, approximateCoordinates } from '@/features/discovery/geo'

import { notificationsApi } from '../api'

const REPORT_INTERVAL_MS = 15 * 60 * 1000
const REPORT_THROTTLE_MS = REPORT_INTERVAL_MS
export const POSITION_STORAGE_KEY = 'kuniri.notifications.position.v1'

/**
 * Pure throttle decision, exported for tests: a report is due when no report
 * is stored, or the last one is older than the reporting interval.
 */
export function shouldReportPosition(lastReportedAt: number, now: number): boolean {
  return now - lastReportedAt >= REPORT_THROTTLE_MS
}

type EnabledCheck = Promise<boolean>

let enabledCheck: EnabledCheck | null = null

// One settings read per session decides whether positions may be sent at all:
// the device of a user who disabled the feature must not transmit
// coordinates. A failed check fails closed (no capture).
function featureEnabled(): EnabledCheck {
  if (!enabledCheck) {
    enabledCheck = notificationsApi
      .getProximitySettings()
      .then((response) => response.settings.enabled)
      .catch(() => false)
  }
  return enabledCheck
}

function lastReport(): number {
  try {
    return Number(localStorage.getItem(POSITION_STORAGE_KEY) ?? 0)
  } catch {
    return 0
  }
}

function markReported(): void {
  try {
    localStorage.setItem(POSITION_STORAGE_KEY, String(Date.now()))
  } catch {
    // Storage being unavailable must not break capture.
  }
}

/**
 * Foreground position capture for proximity notifications (D-074).
 *
 * A web app cannot track position in the background, so this hook reports the
 * device position while the app is used: on mount, when the tab becomes
 * visible again, and at most every 15 minutes. Coordinates are rounded to
 * about 100 m before they leave the device and are never logged. The backend
 * re-checks that the feature is enabled and drops the call otherwise.
 */
export function useProximityLocation(): void {
  const busy = useRef(false)

  useEffect(() => {
    let timer = 0

    const report = async () => {
      if (busy.current) return
      if (useSessionStore.getState().status !== 'authenticated') return
      // No geolocation API (tests, unsupported browsers): nothing to report
      // and no reason to touch the API at all.
      if (typeof navigator === 'undefined' || !navigator.geolocation) return
      if (!shouldReportPosition(lastReport(), Date.now())) return
      busy.current = true
      try {
        if (!(await featureEnabled())) return
        const coordinates = await currentCoordinates()
        await notificationsApi.recordLastPosition(
          approximateCoordinates(coordinates),
        )
        markReported()
      } catch {
        // Best-effort: denied/timeout/offline simply wait for the next
        // trigger. Coordinates and errors are never logged.
      } finally {
        busy.current = false
      }
    }

    const schedule = () => {
      void report()
    }

    schedule()
    timer = window.setInterval(schedule, REPORT_INTERVAL_MS)
    document.addEventListener('visibilitychange', schedule)
    window.addEventListener('focus', schedule)

    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', schedule)
      window.removeEventListener('focus', schedule)
    }
  }, [])
}
